import {
  BiosimilarConfigDoc,
  BiosimilarDoseConversion,
  BiosimilarFamily,
  BiosimilarFamilyType,
  DoseConversionRow,
  ReverseConversionRow,
} from "../types";

/**
 * Stable JSON signature for dirty-state comparison.
 *
 * @param doc - Biosimilar config document
 * @returns Serialized snapshot of editable fields
 */
export const configSignature = (doc: BiosimilarConfigDoc): string =>
  JSON.stringify({
    enabled: doc.enabled !== false,
    version: doc.version,
    org_ids: doc.org_ids ?? [],
    denial_outcomes: doc.denial_outcomes ?? [],
    families: doc.families ?? [],
  });

/**
 * Filters families by family name (case-insensitive).
 *
 * @param families - All family definitions
 * @param query - Search text from the filter input
 * @returns Families whose label matches the query
 */
export const filterFamilies = (
  families: BiosimilarFamily[],
  query: string,
): BiosimilarFamily[] => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return families;
  }
  return families.filter((family) =>
    (family.family ?? "").toLowerCase().includes(normalized),
  );
};

/**
 * Counts families that use dose conversions.
 *
 * @param families - Family definitions
 * @returns Number of families with dose conversion data
 */
export const countDoseConversionFamilies = (
  families: BiosimilarFamily[],
): number =>
  families.filter(
    (family) =>
      family.type === "dose_converted" ||
      Object.keys(family.dose_conversions ?? {}).length > 0,
  ).length;

/**
 * Whether a family should display the dose-converted badge.
 *
 * @param family - Family definition
 * @returns True when conversions exist or type is dose_converted
 */
export const isDoseConvertedFamily = (family: BiosimilarFamily): boolean =>
  family.type === "dose_converted" ||
  Object.keys(family.dose_conversions ?? {}).length > 0;

/**
 * Maps a dose_conversions record to editable rows.
 *
 * @param conversions - Forward conversion map from Firestore
 * @returns Rows for the conversion editor
 */
export const toConversionRows = (
  conversions: Record<string, BiosimilarDoseConversion> = {},
): DoseConversionRow[] =>
  Object.entries(conversions).map(([fromRegimen, conversion]) => ({
    fromRegimen,
    dose: conversion.dose ?? "",
    package_requirement: conversion.package_requirement ?? "",
  }));

/**
 * Maps editable conversion rows back to a Firestore record.
 *
 * @param rows - Conversion editor rows
 * @returns Forward conversion map
 */
export const fromConversionRows = (
  rows: DoseConversionRow[],
): Record<string, BiosimilarDoseConversion> => {
  const result: Record<string, BiosimilarDoseConversion> = {};
  rows.forEach((row) => {
    const key = row.fromRegimen.trim();
    if (!key) {
      return;
    }
    result[key] = {
      dose: row.dose.trim() || undefined,
      package_requirement: row.package_requirement.trim() || undefined,
    };
  });
  return result;
};

/**
 * Maps reverse_conversions to editable rows.
 *
 * @param conversions - Reverse conversion map from Firestore
 * @returns Rows for the reverse conversion editor
 */
export const toReverseConversionRows = (
  conversions: Record<string, string> = {},
): ReverseConversionRow[] =>
  Object.entries(conversions).map(([fromRegimen, toRegimen]) => ({
    fromRegimen,
    toRegimen,
  }));

/**
 * Maps reverse conversion rows back to a Firestore record.
 *
 * @param rows - Reverse conversion editor rows
 * @returns Reverse conversion map
 */
export const fromReverseConversionRows = (
  rows: ReverseConversionRow[],
): Record<string, string> => {
  const result: Record<string, string> = {};
  rows.forEach((row) => {
    const from = row.fromRegimen.trim();
    const to = row.toRegimen.trim();
    if (from && to) {
      result[from] = to;
    }
  });
  return result;
};

/**
 * Whether a family name already exists (case-insensitive).
 *
 * @param families - Existing family definitions
 * @param name - Candidate family label
 * @returns True when another family already uses this label
 */
export const hasFamilyName = (
  families: BiosimilarFamily[],
  name: string,
): boolean => {
  const normalized = name.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return families.some(
    (family) => (family.family ?? "").trim().toLowerCase() === normalized,
  );
};

/**
 * Input for building a new family from the add-family modal.
 */
export interface NewFamilyFormInput {
  /** Family display name. */
  family: string;
  /** Matching strategy. */
  type: BiosimilarFamilyType;
  /** Interchangeable product names. */
  members: string[];
  /** Trigger drug for dose-converted families. */
  original_drug: string;
}

/**
 * Builds a Firestore family object from the add-family modal form.
 *
 * @param input - Validated modal field values
 * @returns Family ready to append to the config document
 */
export const buildFamilyFromForm = (
  input: NewFamilyFormInput,
): BiosimilarFamily => {
  const members = input.members.map((member) => member.trim()).filter(Boolean);
  const family: BiosimilarFamily = {
    family: input.family.trim(),
    type: input.type,
    members,
  };
  if (input.type === "dose_converted") {
    family.original_drug = input.original_drug.trim() || undefined;
    family.dose_conversions = {};
  }
  return family;
};
